import React, { useCallback, useEffect, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import {
  createSignatory,
  deleteSignatory,
  fetchSignatories,
  setDefaultSignatory,
  updateSignatory,
} from "../../services/signatoryService";
import { useToast } from "../../contexts/ToastContext";

const PRODUCTS = [
  { value: "all", label: "All products" },
  { value: "permit", label: "SimplePermit" },
  { value: "check", label: "SimpleCheck" },
  { value: "spect", label: "SimpleSpect" },
];

const productLabel = (value) => PRODUCTS.find((p) => p.value === value)?.label || "All products";

const EMPTY_FORM = { id: null, fullName: "", product: "all", isDefault: false };

function SignatureSettings() {
  const [signatories, setSignatories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setSignatories(await fetchSignatories());
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Could not load your signatures. Please refresh, or contact support if this keeps happening."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setShowModal(true);
  };

  const openEdit = (signatory) => {
    setForm({
      id: signatory.id,
      fullName: signatory.fullName,
      product: signatory.product || "all",
      isDefault: !!signatory.isDefault,
    });
    setFormError("");
    setShowModal(true);
  };

  const handleField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) {
      setFormError("Please enter the name that should appear on the document.");
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      const payload = {
        fullName: form.fullName.trim(),
        product: form.product,
      };

      if (form.id) {
        await updateSignatory(form.id, payload);
        if (form.isDefault) await setDefaultSignatory(form.id);
        showToast("Signature updated.", "success");
      } else {
        await createSignatory({ ...payload, isDefault: form.isDefault });
        showToast("Signature added.", "success");
      }

      setShowModal(false);
      await load();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Could not save this signature. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleMakeDefault = async (signatory) => {
    try {
      await setDefaultSignatory(signatory.id);
      showToast(`${signatory.fullName} is now your default signature.`, "success");
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not set the default.", "error");
    }
  };

  const handleRemove = async (signatory) => {
    try {
      await deleteSignatory(signatory.id);
      showToast(`${signatory.fullName} removed.`, "success");
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not remove this signature.", "error");
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5 py-5">
        <Spinner animation="border" role="status" variant="primary" className="mb-2" />
        <p className="text-muted">Loading your signatures...</p>
      </div>
    );
  }

  return (
    <div className="subMain-content">
      <div>
        <h1 className="mt-4">Signatures</h1>
        <p className="text-muted">
          These are the people who sign your community's approvals and inspection records. Whoever you
          add here is who your reviewers can choose from when they approve an application &mdash; the
          name prints on the PDF your applicants receive.
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0" style={{ maxWidth: "900px" }}>
        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-semibold text-dark">Authorised signatures</h5>
          <Button variant="primary" size="sm" onClick={openAdd}>
            Add signature
          </Button>
        </Card.Header>

        <Card.Body className="p-0">
          {signatories.length === 0 ? (
            <div className="text-center py-5 px-4">
              <p className="fw-semibold mb-1">You haven't added any signatures yet</p>
              <p className="text-muted small mb-3">
                Until you add at least one, your team can't approve applications &mdash; approvals have to
                be signed by someone from your community.
              </p>
              <Button variant="primary" onClick={openAdd}>
                Add your first signature
              </Button>
            </div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">Name</th>
                  <th>Applies to</th>
                  <th className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {signatories.map((s) => (
                  <tr key={s.id}>
                    <td className="ps-4 fw-medium">
                      {s.fullName}
                      {!!s.isDefault && (
                        <Badge bg="success" className="ms-2 fw-normal">
                          Default
                        </Badge>
                      )}
                    </td>
                    <td className="text-muted">{productLabel(s.product)}</td>
                    <td className="text-end pe-4">
                      {!s.isDefault && (
                        <Button
                          variant="link"
                          size="sm"
                          className="text-decoration-none"
                          onClick={() => handleMakeDefault(s)}
                        >
                          Make default
                        </Button>
                      )}
                      <Button
                        variant="link"
                        size="sm"
                        className="text-decoration-none"
                        onClick={() => openEdit(s)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-decoration-none text-danger"
                        onClick={() => handleRemove(s)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <p className="text-muted small mt-3" style={{ maxWidth: "900px" }}>
        Removing someone here stops them appearing in the dropdown from now on. Documents they've
        already signed keep their name.
      </p>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="h5">{form.id ? "Edit signature" : "Add signature"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}

            <Form.Group className="mb-3" controlId="signatoryFullName">
              <Form.Label className="small text-muted mb-1 fw-medium">
                Name<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={form.fullName}
                onChange={(e) => handleField("fullName", e.target.value)}
                placeholder="e.g. Jane Roe"
                maxLength={150}
                disabled={saving}
                autoFocus
              />
              <Form.Text className="text-muted small">Exactly as it should print on the document.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="signatoryProduct">
              <Form.Label className="small text-muted mb-1 fw-medium">Applies to</Form.Label>
              <Form.Select
                value={form.product}
                onChange={(e) => handleField("product", e.target.value)}
                disabled={saving}
              >
                {PRODUCTS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted small">
                Pick a single product if this person only signs one kind of document.
              </Form.Text>
            </Form.Group>

            <Row>
              <Col>
                <Form.Check
                  type="checkbox"
                  id="signatoryDefault"
                  label="Make this the default signature"
                  checked={form.isDefault}
                  onChange={(e) => handleField("isDefault", e.target.checked)}
                  disabled={saving}
                />
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : form.id ? "Save changes" : "Add signature"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default SignatureSettings;
